import { ArrowRight, Compass, HardHat, Building2, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import useFAQSchema from "../hooks/useFAQSchema";
import { FAQ_TEAM } from "../data/faqs";

/* ──────────────────────────────────────────────────────────────────
 *  /team — The People Behind SRM
 *  Section rhythm:
 *    1. Hero                  white
 *    2. Leadership Team       white
 *    3. Collective Expertise  white
 *    4. CTA                   navy
 * ────────────────────────────────────────────────────────────────── */

const EYEBROW = "font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]";

const TEAM = [
    {
        name: "Andy Nagy",
        role: "General Manager",
        image: "/assets/team/andy.jpg",
        bio: [
            "Andy leads SRM's day-to-day operations, ensuring every engagement is delivered with technical rigour and committee-first reporting.",
            "His background spans construction project delivery, contractor management and strata advisory work — the accountability link between fieldwork, reporting and committee outcomes.",
        ],
    },
    {
        name: "Joe Jenkins",
        role: "Roof Assessment Lead",
        image: "/assets/team/joe.jpg",
        bio: [
            "Joe heads SRM's on-site assessment program with hands-on roofing and building experience across residential, commercial and high-density strata assets.",
            "He brings a builder's understanding of how roof systems fail — translating observed condition into clear, photographic evidence committees can act on.",
        ],
    },
    {
        name: "Amanda Belot",
        role: "Business Development Manager",
        image: "/assets/team/amanda.jpg",
        bio: [
            "Amanda is the first point of contact for strata committees, building managers and strata managers engaging SRM.",
            "She brings extensive relationship management experience across the strata sector — ensuring every enquiry is scoped accurately and matched to the right SRM service.",
        ],
    },
];

const COLLECTIVE = [
    {
        icon: Compass,
        title: "Independent Advice",
        body: "Every SRM recommendation is grounded in observed roof condition, so committees receive advice that serves the building first.",
    },
    {
        icon: HardHat,
        title: "Builder-Level Expertise",
        body: "Decades of construction, roofing and project delivery experience applied to roof condition, defect diagnosis and long-term asset protection.",
    },
    {
        icon: Building2,
        title: "Strata-Native Understanding",
        body: "Committee governance, insurance claim context and capital works planning built into every report — written for the people making the decisions.",
    },
    {
        icon: MapPin,
        title: "National Capability",
        body: "A consistent independent service standard for strata communities, body corporates and managed buildings across Australia.",
    },
];

/* === 1. Hero ====================================================== */
function TeamHero() {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="team-hero" className="bg-white">
            <div className="container-srm pt-24 pb-16 lg:pt-32 lg:pb-20">
                <div className="reveal max-w-[760px] mx-auto text-center">
                    <div className={EYEBROW}>Our Team</div>
                    <span aria-hidden className="mx-auto mt-5 block h-[3px] w-16 bg-srm-gold" />
                    <h1
                        data-testid="team-hero-headline"
                        className="mt-8 font-display font-bold text-[2.1rem] sm:text-[2.6rem] lg:text-[3.15rem] tracking-[-0.024em] leading-[1.1] text-srm-navy text-balance"
                    >
                        The people behind every SRM engagement.
                    </h1>
                    <p className="mt-8 text-[16px] lg:text-[17.5px] text-srm-body leading-[1.85]">
                        SRM&apos;s leadership team combines decades of construction,
                        roofing and strata experience to deliver independent advice
                        committees can trust. Every recommendation is evidence-based,
                        commercially independent and focused on protecting long-term
                        building performance.
                    </p>
                </div>
            </div>
        </section>
    );
}

/* === 2. Leadership Team ========================================== */
function TeamGrid() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="team-grid"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-24 lg:py-32">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Leadership Team</div>
                    <span aria-hidden className="mx-auto mt-5 block h-[3px] w-16 bg-srm-gold" />
                    <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.75rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        Meet the Leadership Team.
                    </h2>
                </div>

                <div className="mt-12 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
                    {TEAM.map((m, i) => (
                        <article
                            key={m.name}
                            data-testid={`team-card-${i}`}
                            className="reveal group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(15,40,86,0.14)]"
                            style={{ transitionDelay: `${i * 80}ms` }}
                        >
                            {/* gold rule top — expands on hover */}
                            <span
                                aria-hidden
                                className="pointer-events-none absolute left-0 top-0 h-[3px] w-16 bg-srm-gold transition-all duration-500 group-hover:w-full z-10"
                            />

                            {/* Portrait — identical aspect ratio across all cards */}
                            <div className="relative w-full aspect-[4/5] overflow-hidden bg-[#EEF1F5]">
                                <img
                                    src={m.image}
                                    alt={`${m.name} — ${m.role}`}
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                                    style={{ objectPosition: "50% 22%" }}
                                    loading="lazy"
                                />
                            </div>

                            {/* Identity + Bio */}
                            <div className="flex flex-1 flex-col px-8 lg:px-9 pt-8 pb-9">
                                <h3
                                    data-testid={`team-name-${i}`}
                                    className="font-display font-bold text-srm-navy text-[1.4rem] lg:text-[1.55rem] tracking-[-0.012em] leading-tight"
                                >
                                    {m.name}
                                </h3>
                                <div
                                    data-testid={`team-role-${i}`}
                                    className="mt-3 font-display font-semibold tracking-[0.2em] uppercase text-srm-gold text-[11px] lg:text-[11.5px]"
                                >
                                    {m.role}
                                </div>
                                <span aria-hidden className="mt-5 block h-[2px] w-10 bg-srm-gold" />
                                <div
                                    data-testid={`team-bio-${i}`}
                                    className="mt-6 space-y-4 text-[14.5px] lg:text-[15px] text-srm-body leading-[1.8]"
                                >
                                    {m.bio.map((para) => (
                                        <p key={para.slice(0, 24)}>{para}</p>
                                    ))}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* === 3. Collective Expertise ===================================== */
function CollectiveExpertise() {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="team-collective" className="bg-white border-t border-srm-line">
            <div className="container-srm py-24 lg:py-32">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Collective Expertise</div>
                    <span aria-hidden className="mx-auto mt-5 block h-[3px] w-16 bg-srm-gold" />
                    <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.75rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        Why committees trust the SRM team.
                    </h2>
                    <p className="mt-8 text-[15.5px] lg:text-[17px] text-srm-body leading-[1.85]">
                        Independent advice carries weight only when it is backed by
                        genuine technical capability and a clear understanding of how
                        strata communities make decisions.
                    </p>
                </div>

                <div className="mt-16 lg:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7 lg:gap-8">
                    {COLLECTIVE.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <article
                                key={c.title}
                                data-testid={`collective-${i}`}
                                className="reveal group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white p-9 lg:p-10 shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(15,40,86,0.14)]"
                                style={{ transitionDelay: `${i * 80}ms` }}
                            >
                                <span
                                    aria-hidden
                                    className="pointer-events-none absolute left-0 top-0 h-[3px] w-16 bg-srm-gold transition-all duration-500 group-hover:w-full"
                                />
                                <span className="inline-flex h-[66px] w-[66px] items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold transition-all duration-500 group-hover:bg-srm-gold/10">
                                    <Icon className="h-[29px] w-[29px]" strokeWidth={1.5} />
                                </span>
                                <h3 className="mt-8 font-display font-bold text-srm-navy text-[1.2rem] lg:text-[1.25rem] tracking-[-0.005em] leading-snug">
                                    {c.title}
                                </h3>
                                <p className="mt-4 text-[14.5px] lg:text-[15px] text-srm-body leading-[1.75]">
                                    {c.body}
                                </p>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* === 4. Final CTA ================================================ */
function TeamCTA() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="team-cta"
            className="relative overflow-hidden"
            style={{ backgroundColor: "#163E7A" }}
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(201,165,92,0.10) 0%, rgba(201,165,92,0) 70%)" }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute -bottom-40 -left-20 h-[420px] w-[420px] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(201,165,92,0.07) 0%, rgba(201,165,92,0) 70%)" }}
            />
            <div className="container-srm relative py-24 lg:py-32">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <span aria-hidden className="mx-auto block h-[3px] w-16 bg-srm-gold" />
                    <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.5rem] lg:text-[3rem] tracking-[-0.024em] leading-[1.1] text-white text-balance">
                        Work with an independent team that puts your committee first.
                    </h2>
                    <p className="mt-8 text-[15.5px] lg:text-[17.5px] text-white/80 leading-[1.85] max-w-2xl mx-auto">
                        Independent advice. Evidence-based reporting. Builder-level
                        expertise. A national service standard built around the strata
                        committees we work for.
                    </p>
                    <div className="mt-12 flex justify-center">
                        <Link
                            to="/contact"
                            data-testid="team-cta-primary"
                            className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[12px] font-semibold tracking-[0.2em] uppercase px-10 h-[56px] border border-srm-gold shadow-[0_10px_30px_rgba(201,165,92,0.30)] hover:bg-srm-goldDark hover:border-srm-goldDark hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(201,165,92,0.40)] transition-all duration-300"
                        >
                            Speak with the Team
                            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* === Page composition ============================================ */
export default function Team() {
    usePageMeta({
        title: "Our Team — SRM Strata Roof Management",
        description:
            "Meet the SRM team — independent roof asset management advisors for Australian strata communities. Builder-level expertise, evidence-based reporting, committee-first advice.",
        path: "/team",
        breadcrumbs: [
            { name: "Home", url: "/" },
            { name: "Our Team", url: "/team" },
        ],
    });
    useFAQSchema("team", FAQ_TEAM);

    return (
        <div data-testid="team-page" className="min-h-screen bg-white">
            <Navbar />
            <main id="main">
                <TeamHero />
                <TeamGrid />
                <CollectiveExpertise />
                <TeamCTA />
            </main>
            <Footer />
        </div>
    );
}
